import { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Phone, Mail, Send, Star, Shield, Clock, CheckCircle2, Award, Zap, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import zenithLogo from "@/assets/zenith-logo.webp";
import heroBg from "@/assets/hero-bg.jpg";

const benefits = [
  { icon: Shield, title: "Licensed & Insured", desc: "Fully licensed in NM with comprehensive liability coverage." },
  { icon: Clock, title: "Fast Turnaround", desc: "Most roofing projects completed in 1–3 days." },
  { icon: Award, title: "A+ BBB Rating", desc: "Top-rated on BBB, Google, and HomeAdvisor." },
  { icon: Zap, title: "Free Estimates", desc: "No-obligation inspections and transparent pricing." },
];

const reviews = [
  { name: "Maria G.", text: "Zenith replaced our roof after a hailstorm. Fast, professional, and the price was fair. Highly recommend!", rating: 5 },
  { name: "Robert T.", text: "Best contractor experience I've had. They showed up on time, did quality work, and cleaned up after themselves.", rating: 5 },
  { name: "Sandra L.", text: "Our solar panels have cut our electric bill by 60%. Zenith handled everything from permits to installation.", rating: 5 },
];

const LandingPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [service, setService] = useState("");
  const lastSubmit = useRef(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastSubmit.current < 30000) {
      toast({ title: "Please wait", description: "You can submit again in a few seconds.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const honeypot = (formData.get("website") as string || "").trim();
    if (honeypot) {
      setLoading(false);
      toast({ title: "Request received!", description: "We'll call you shortly." });
      return;
    }

    const name = (formData.get("name") as string).trim();
    const phone = (formData.get("phone") as string).trim();
    const email = (formData.get("email") as string).trim();
    const message = (formData.get("message") as string || "").trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      setLoading(false);
      return;
    }
    const phoneRegex = /^[\d\s\-\(\)\+]{7,20}$/;
    if (!phoneRegex.test(phone)) {
      toast({ title: "Invalid phone", description: "Please enter a valid phone number.", variant: "destructive" });
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { name, phone, email, service: service || "other", message, source: "landing-page" },
      });
      if (error) throw error;
      lastSubmit.current = now;
      toast({ title: "Request received!", description: "We'll call you shortly." });
      form.reset();
      setService("");
    } catch {
      toast({ title: "Something went wrong", description: "Please call us directly at (505) 264-0170.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Get a Free Roofing & Solar Estimate | Zenith Solutions Albuquerque NM</title>
        <meta name="description" content="Get a free roofing, solar, or construction estimate from Zenith Solutions in Albuquerque NM. Licensed & insured, A+ BBB rated, 1000+ completed projects. Call (505) 264-0170." />
        <meta name="keywords" content="free roofing estimate Albuquerque, free solar estimate NM, roofing contractor Albuquerque, solar installation New Mexico" />
        <link rel="canonical" href="https://zenithsolutionsnm.com/get-estimate" />

        <meta property="og:title" content="Get a Free Estimate | Zenith Solutions - Albuquerque Roofing & Solar" />
        <meta property="og:description" content="Free roofing, solar & construction estimates. Licensed, A+ BBB rated, 1000+ projects in NM." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://zenithsolutionsnm.com/get-estimate" />
        <meta property="og:image" content="https://zenithsolutionsnm.com/og-image.jpg" />

        <meta name="geo.region" content="US-NM" />
        <meta name="geo.placename" content="Albuquerque" />

        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Hero with form */}
      <section className="relative min-h-screen flex items-center">
        <img src={heroBg} alt="Albuquerque home with new roof and solar panels installed by Zenith Solutions" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 hero-gradient" />

        <div className="relative z-10 container mx-auto px-4 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left: headline */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <img src={zenithLogo} alt="Zenith Solutions - Albuquerque Roofing and Solar Company" className="h-14 mb-8" width={200} height={56} />
              <div className="flex gap-3 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" aria-hidden="true" />
                ))}
                <span className="text-white/80 text-sm font-medium ml-1">150+ 5-Star Reviews</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-primary-foreground leading-[1.1] mb-6 drop-shadow-lg">
                New Mexico's #1 Roofing & Solar Company
              </h1>
              <p className="text-lg text-white/80 mb-8 max-w-lg leading-relaxed drop-shadow-md">
                Licensed, insured, and trusted by 1,000+ homeowners across Albuquerque. Get your <strong className="text-white">FREE</strong> no-obligation estimate today.
              </p>
              <a href="tel:5052640170" aria-label="Call Zenith Solutions at (505) 264-0170">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg font-bold h-16 px-10 gap-3 shadow-xl">
                  <Phone className="h-6 w-6" aria-hidden="true" /> Call Now (505) 264-0170
                </Button>
              </a>
              <div className="mt-4">
                <Link to="/tools/estimate-calculator">
                  <Button variant="outline" size="lg" className="text-lg font-bold h-14 px-8 gap-2 bg-white/10 border-white/40 text-white hover:bg-white hover:text-foreground">
                    <Calculator className="h-5 w-5" aria-hidden="true" /> Estimate Calculator
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right: lead form */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              <form
                onSubmit={handleSubmit}
                className="bg-card text-card-foreground rounded-2xl p-8 shadow-2xl space-y-4"
                aria-label="Request a free estimate"
              >
                <div className="text-center mb-2">
                  <h2 className="text-xl font-black text-foreground">Get Your FREE Estimate</h2>
                  <p className="text-sm text-muted-foreground">We respond within 2 hours during business hours</p>
                </div>
                <Input name="name" placeholder="Full Name *" required className="h-12" maxLength={100} autoComplete="name" aria-label="Full name" />
                <Input name="phone" type="tel" placeholder="Phone Number *" required className="h-12" maxLength={20} autoComplete="tel" aria-label="Phone number" />
                <Input name="email" type="email" placeholder="Email *" required className="h-12" maxLength={255} autoComplete="email" aria-label="Email address" />
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <Input name="website" tabIndex={-1} autoComplete="off" />
                </div>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger className="h-12" aria-label="Select service type"><SelectValue placeholder="What do you need help with?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="roofing">Roofing</SelectItem>
                    <SelectItem value="construction">General Construction</SelectItem>
                    <SelectItem value="solar">Solar</SelectItem>
                    <SelectItem value="mold">Mold & Water Mitigation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea name="message" placeholder="Tell us about your project (optional)" rows={3} className="resize-none" maxLength={1000} aria-label="Project details" />
                
                {/* SMS Consent Checkbox */}
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox id="sms-consent" name="sms_consent" className="mt-0.5" />
                  <label htmlFor="sms-consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    I agree to receive text messages from <strong className="text-foreground">Zenith Solutions</strong> at the phone number provided. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out. By opting in, you agree to our{" "}
                    <Link to="/terms-of-service" className="text-primary underline hover:text-primary/80" target="_blank">Terms</Link>{" "}and{" "}
                    <Link to="/privacy-policy" className="text-primary underline hover:text-primary/80" target="_blank">Privacy Policy</Link>.
                  </label>
                </div>

                <Button type="submit" disabled={loading} size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-14 text-base gap-2">
                  <Send className="h-5 w-5" aria-hidden="true" /> {loading ? "Sending..." : "Get My Free Estimate"}
                </Button>
                <div className="mt-3 text-center">
                  <Link to="/tools/estimate-calculator">
                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-primary">
                      or get an estimate online
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-center text-muted-foreground">No spam. No pressure. Just honest guidance.</p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits strip */}
      <section className="bg-primary text-primary-foreground py-16 px-4" aria-label="Why choose Zenith Solutions">
        <div className="container mx-auto max-w-5xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 mb-4">
                  <b.icon className="h-7 w-7" aria-hidden="true" />
                </div>
                <h3 className="font-bold text-lg mb-1">{b.title}</h3>
                <p className="text-primary-foreground/70 text-sm">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="bg-section-mid py-16 px-4" aria-label="Customer testimonials">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-black text-foreground text-center mb-10">What Our Customers Say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <motion.div
                key={r.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl p-6 shadow-sm border"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="text-sm text-muted-foreground leading-relaxed mb-4">"{r.text}"</blockquote>
                <p className="font-bold text-foreground text-sm">{r.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-section-light py-16 px-4" aria-label="Services offered">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-black text-foreground mb-8">Our Services</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            {[
              "Residential & Commercial Roofing",
              "Storm Damage Repair & Insurance Claims",
              "Solar Panel Installation",
              "General Construction & Remodeling",
              "Mold & Water Mitigation",
              "Roof Inspections & Maintenance",
            ].map((s) => (
              <div key={s} className="flex items-center gap-3 bg-card border rounded-lg p-4">
                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                <span className="font-medium text-foreground text-sm">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-foreground text-background py-16 px-4" aria-label="Contact us">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">
            Call us now for an immediate consultation or scroll up to fill out our quick estimate form.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5052640170" aria-label="Call Zenith Solutions">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg font-bold h-16 px-10 gap-3 w-full sm:w-auto">
                <Phone className="h-6 w-6" aria-hidden="true" /> (505) 264-0170
              </Button>
            </a>
            <a href="mailto:info@zenithsolutionsnm.com" aria-label="Email Zenith Solutions">
              <Button size="lg" className="bg-white/10 border-2 border-white/30 text-white hover:bg-white/20 hover:text-white text-lg font-bold h-16 px-10 gap-3 w-full sm:w-auto">
                <Mail className="h-6 w-6" aria-hidden="true" /> Email Us
              </Button>
            </a>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10 text-xs text-white/40">
            <p>©2026 Zenith Solutions. All rights reserved. | Licensed & Insured in New Mexico</p>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-primary text-primary-foreground">
        <a href="tel:5052640170" className="flex items-center justify-center gap-2 py-3 font-bold text-sm" aria-label="Call Zenith Solutions now">
          <Phone className="h-4 w-4" aria-hidden="true" /> Call Now (505) 264-0170
        </a>
      </div>
    </>
  );
};

export default LandingPage;
